import React, { useState, useEffect, useCallback } from 'react';
import { verificationAPI } from '../lib/api';
import { toast } from 'sonner';
import { ShieldCheck, Briefcase } from 'lucide-react';

const clearanceStyle = (cs) => {
  const map = {
    cleared: { label: 'Cleared', c: '#059669', b: 'rgba(16,185,129,0.12)' },
    pending: { label: 'Pending', c: '#64748B', b: 'rgba(100,116,139,0.12)' },
    action_required: { label: 'Action Required', c: '#D97706', b: 'rgba(217,119,6,0.12)' },
    blocked: { label: 'Blocked', c: '#DC2626', b: 'rgba(220,38,38,0.1)' },
    expired: { label: 'Expired', c: '#9333EA', b: 'rgba(147,51,234,0.1)' },
  };
  return map[cs] || map.pending;
};

const KPI = ({ label, value, color }) => (
  <div style={{ flex: 1, minWidth: 120, background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.1rem 1.25rem' }} data-testid={`compliance-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div style={{ fontSize: '1.75rem', fontWeight: 800, color }}>{value}</div>
    <div style={{ fontSize: '0.8125rem', color: '#64748B', marginTop: '0.125rem' }}>{label}</div>
  </div>
);

export default function CompliancePage() {
  const [data, setData] = useState({ summary: {}, requests: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await verificationAPI.getDashboard();
      setData(res.data || { summary: {}, requests: [] });
    } catch (e) {
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }} data-testid="compliance-loading">Loading compliance overview...</div>;
  }

  const s = data.summary || {};
  const total = s.total || 0;
  const clearanceRate = total > 0 ? Math.round(((s.cleared || 0) / total) * 100) : 0;

  return (
    <div style={{ padding: '2rem', maxWidth: 1200, margin: '0 auto' }} data-testid="compliance-page">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#002A38', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <ShieldCheck size={26} color="#0F766E" /> Compliance
        </h1>
        <p style={{ color: '#64748B', fontSize: '0.9375rem', marginTop: '0.25rem' }} data-testid="compliance-subtitle">
          Verification clearance oversight across all your hiring — {clearanceRate}% cleared
        </p>
      </div>

      {/* Clearance rate bar */}
      <div style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#334155' }}>Overall clearance rate</span>
          <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0F766E' }} data-testid="compliance-clearance-rate">{clearanceRate}%</span>
        </div>
        <div style={{ height: 10, borderRadius: 999, background: '#F1F5F9', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${clearanceRate}%`, background: 'linear-gradient(90deg,#0F766E,#10B981)', transition: 'width 0.4s' }} />
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.75rem' }}>
        <KPI label="Cleared" value={s.cleared || 0} color="#059669" />
        <KPI label="Pending" value={s.pending || 0} color="#64748B" />
        <KPI label="Action Required" value={s.action_required || 0} color="#D97706" />
        <KPI label="Blocked" value={s.blocked || 0} color="#DC2626" />
        <KPI label="Expired" value={s.expired || 0} color="#9333EA" />
      </div>

      <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, color: '#002A38', marginBottom: '0.875rem' }}>Verification records</h2>

      {(!data.requests || data.requests.length === 0) ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', background: '#FAF7F2', borderRadius: 16, border: '1px dashed #CBD5E1' }} data-testid="compliance-empty">
          <ShieldCheck size={40} color="#94A3B8" style={{ margin: '0 auto 1rem' }} />
          <div style={{ fontWeight: 700, color: '#334155', fontSize: '1.0625rem' }}>No verification records yet</div>
          <p style={{ color: '#64748B', fontSize: '0.875rem', marginTop: '0.5rem' }}>Send a background verification request from the BGV tab to begin tracking compliance.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {data.requests.map((r) => {
            const cs = clearanceStyle(r.clearance_status || 'pending');
            return (
              <div key={r._id} data-testid={`compliance-row-${r._id}`} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.25rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#002A38', fontSize: '0.9375rem' }}>{r.candidate_name || r.candidate_email}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#64748B', fontSize: '0.8125rem', marginTop: '0.1875rem' }}>
                    <Briefcase size={13} /> {r.job_title || '—'}
                  </div>
                </div>
                {r.created_at && <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{new Date(r.created_at).toLocaleDateString()}</span>}
                <span style={{ padding: '0.25rem 0.75rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, background: cs.b, color: cs.c }}>{cs.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
